import * as api from "./api";

/** The parts of the Maps JavaScript API that the web app uses. */
export interface LatLngLike {
  lat: number | (() => number);
  lng: number | (() => number);
}
export interface GoogleMap {
  setMapTypeId(id: "roadmap" | "hybrid"): void;
  setCenter(point: api.LatLng): void;
  getCenter(): LatLngLike | null | undefined;
  fitBounds(bounds: unknown, padding?: number): void;
  getZoom(): number | undefined;
  setZoom(zoom: number): void;
  getBounds(): { getSouthWest(): LatLngLike; getNorthEast(): LatLngLike } | null | undefined;
  addListener(event: string, handler: () => void): { remove(): void };
}
export interface Polyline {
  setMap(map: GoogleMap | null): void;
  setPath(path: api.LatLng[]): void;
  setOptions(options: Record<string, unknown>): void;
  addListener(event: string, handler: () => void): { remove(): void };
}
export interface PolygonPath {
  getArray(): LatLngLike[];
  removeAt(index: number): void;
  addListener(event: "insert_at" | "remove_at" | "set_at", handler: () => void): { remove(): void };
}
export interface Polygon {
  setMap(map: GoogleMap | null): void;
  getPath(): PolygonPath;
  addListener(event: string, handler: (event: { vertex?: number; domEvent?: Event }) => void): { remove(): void };
}
export interface Marker {
  position: LatLngLike | null | undefined;
  map: GoogleMap | null;
  gmpDraggable: boolean;
  content?: Node | null;
  title: string;
  zIndex?: number | null;
  addListener(event: string, handler: () => void): { remove(): void };
}
interface MapsLibrary {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMap;
  LatLngBounds: new () => { extend(point: api.LatLng): void };
  Polyline: new (options: Record<string, unknown>) => Polyline;
  Polygon: new (options: Record<string, unknown>) => Polygon;
}
interface CoreLibrary {
  LatLngBounds: MapsLibrary["LatLngBounds"];
}
interface MarkerLibrary {
  AdvancedMarkerElement: new (options: Record<string, unknown>) => Marker;
  PinElement: new (options: Record<string, unknown>) => { element: HTMLElement };
}
export interface Maps {
  maps: MapsLibrary;
  marker: MarkerLibrary;
  mapId: string;
}
interface GoogleNamespace {
  maps?: { importLibrary(name: string): Promise<unknown> };
}

declare global {
  interface Window {
    google?: GoogleNamespace;
    gm_authFailure?: () => void;
    platformMapsReady?: () => void;
  }
}

const SCRIPT = "https://maps.googleapis.com/maps/api/js";
// Google draws these in the map element when the key, the billing or the quota fails.
const GOOGLE_ERROR = ".gm-err-container, .dismissButton";
let loading: Promise<Maps> | null = null;
let broken = false;
const failures = new Set<() => void>();

function fail(): void {
  broken = true;
  for (const callback of [...failures]) callback();
}

/** Load the Maps JavaScript API once for the page, with the browser key of the cell. */
export function loadMaps(token: string): Promise<Maps> {
  if (broken) return Promise.reject(new Error("The Maps API failed on this page."));
  loading ??= start(token).catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

/** Call onError one time when Google refuses the key or shows its error in the map element. */
export function watchMapErrors(element: HTMLElement, onError: () => void): () => void {
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    onError();
  };
  if (broken || element.querySelector(GOOGLE_ERROR)) {
    fail();
    once();
    return () => undefined;
  }
  failures.add(once);
  const observer = new MutationObserver(() => {
    if (element.querySelector(GOOGLE_ERROR)) fail();
  });
  observer.observe(element, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
    failures.delete(once);
  };
}

async function start(token: string): Promise<Maps> {
  const config = await api.mapsConfig(token);
  if (!config.browser_key) throw new Error("This cell has no Maps key.");
  if (!window.google?.maps) {
    watchTiles();
    await inject(config.browser_key);
  }
  const google = window.google?.maps;
  if (!google) throw new Error("The Maps API did not load.");
  const [maps, core, marker] = await Promise.all([
    google.importLibrary("maps"),
    google.importLibrary("core"),
    google.importLibrary("marker"),
  ]);
  // LatLngBounds is in the core library, not in the maps library.
  const library = { ...(maps as MapsLibrary), LatLngBounds: (core as CoreLibrary).LatLngBounds };
  return { maps: library, marker: marker as MarkerLibrary, mapId: config.map_id };
}

/** Log a failed Google map request: Google then draws a grey "no imagery" tile and gives no error. */
function watchTiles() {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as (PerformanceResourceTiming & { responseStatus?: number })[]) {
        const status = entry.responseStatus ?? 0;
        if (status < 400 || !/google|gstatic/.test(entry.name)) continue;
        const url = new URL(entry.name);
        console.warn(`map: a Google map request failed with ${status}: ${url.host}${url.pathname}`);
      }
    }).observe({ type: "resource", buffered: true });
  } catch (error) {
    console.info(`map: no request watch in this browser (${error instanceof Error ? error.message : String(error)})`);
  }
}

function inject(key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams({ key, v: "weekly", loading: "async", callback: "platformMapsReady" });
    const script = document.createElement("script");
    script.src = `${SCRIPT}?${params}`;
    script.async = true;
    window.platformMapsReady = () => resolve();
    window.gm_authFailure = () => {
      fail();
      reject(new Error("Google refused the Maps key."));
    };
    script.onerror = () => reject(new Error("The Maps API did not load."));
    document.head.append(script);
  });
}

const read = (value: number | (() => number)) => (typeof value === "function" ? value() : value);

export const pointOf = (at: LatLngLike): api.LatLng => ({ lat: read(at.lat), lng: read(at.lng) });

export function positionOf(marker: Marker): api.LatLng | null {
  const at = marker.position;
  return at ? pointOf(at) : null;
}
